import {
  common,
  firm as FirmNS,
  staff as StaffNS,
  host as HostNS,
} from "@smokeballdev/smokeball-client-sdk";

// Smokeball SDK
export type Client = common.Api;
export type Context = common.Context;
export type Firm = FirmNS.Firm;
export type Staff = StaffNS.Staff;
export type Change = "matters" | "layouts" | "contacts" | "staff" | "firm";
type Observe = Change[];
export type UseSmokeballAppOptions = {
  observe: Observe;
};
export type UseSDKApiOptions = {
  client: Client | null;
  hostType: "windows" | "web" | "local" | undefined;
  isInit: boolean;
  observe?: Observe;
};
export type UseLocalApiOptions = {
  context: Context;
  observe?: Observe;
};
export type LocalSmokeballAppContext = {
  firmId?: string;
  userId?: string;
  staff: Staff[];
  firm: Firm;
};
export type Version = HostNS.Version;
// End Smokeball SDK

export type AccountBillingCountryCode = "US" | "AU" | "GB";
export type Guid = string;
export type SmokeballUserId = Guid;
export type ImportType =
  | "staff"
  | "contact"
  | "matter"
  | "matterType"
  | "matterTag"
  | "matterRelationship"
  | "matterDetail" // Import Type for Matter Critical Date Import Template Key
  | "matterArchive"
  | "matterOpeningBalance"
  | "activity"
  | "unbilledExpense"
  | "unbilledTimeAndFee"
  | "invoice"
  | "invoiceItem"
  | "memo"
  | "task"
  | "event"
  | "safeCustodyPacket"
  | "safeCustodyDocument";
export type TemplateKey =
  // ALL REGIONS
  | "staffImport"
  | "activityImport"
  | "matterTagImport"
  | "matterRelationshipImport"
  | "matterCriticalDateImport" // Import Type is Matter Detail
  | "matterArchiveImport"
  | "memoImport"
  | "taskImport"
  | "eventImport"
  | "safeCustodyPacketImport" // Only visible in AU and GB
  | "safeCustodyDocumentImport" // Only visible in AU and GB
  // US
  | "contactImportUS"
  | "matterImportUS"
  | "matterTypeImportUS"
  | "matterOpeningBalanceImportUS"
  | "unbilledExpenseImportUS"
  | "unbilledTimeAndFeeImportUS"
  | "invoiceImportUS"
  | "invoiceItemImportUS"
  // AU
  | "contactImportAU"
  | "matterImportAU"
  | "matterTypeImportAU"
  | "matterOpeningBalanceImportAU"
  | "unbilledExpenseImportAU"
  | "unbilledTimeAndFeeImportAU"
  | "invoiceImportAU"
  | "invoiceItemImportAU"
  // GB
  | "contactImportGB"
  | "matterImportGB"
  | "matterTypeImportGB"
  | "matterOpeningBalanceImportGB"
  | "unbilledExpenseImportGB"
  | "unbilledTimeAndFeeImportGB"
  | "invoiceImportGB"
  | "invoiceItemImportGB";
export type MigrationSetStatus =
  | "In-progress"
  | "Submitted"
  | "Generating"
  | "Loading"
  | "Failed Validation"
  | "Complete"
  | "Cancelled"
  | "Failed";
export type MigrationSetLoadStatus =
  | "Awaiting-Validation"
  | "Validation-In-Progress"
  | "Validation-Error"
  | "Transform-In-Progress"
  | "Transform-Error"
  | "Awaiting-Load-Approval"
  | "Awaiting-Load"
  | "Load-In-Progress"
  | "Load-Complete"
  | "Export-In-Progress"
  | "Export-Complete"
  | "Export-Error";

export interface MigrationSetResponse {
  accountDetails: AccountDetails;
  migrationSetWithImports: MigrationSetWithImports[];
}

export interface AccountDetails {
  accountId: string;
  firmName: string;
  owner: string;
  userIds: string[];
  addressCountry: string | null;
  addressState: string | null;
  countryCode: string;
  productId: string;
  isInternalAccount: boolean;
}

export interface MigrationSet {
  migrationSetId: number;
  accountId: Guid;
  migrationSetNbr: number;
  accountName: string;
  accountBillingCountry: string;
  accountBillingCountryCode: AccountBillingCountryCode;
  accountBillingStatus: string;
  accountBillingState: string;
  accountSalesforceId: string;
  productId: string;
  status: MigrationSetStatus;
  loadStatus: MigrationSetLoadStatus;
  validationLogFileName: string;
  loadLogFileName: string;
  migrationDataDB: string;
  importTypes: string;
  sourceSystem: SourceSystemType;
  dataOrigin: string;
  exportReason: string;
  dataTypeSubOptions: string;
  partner: string;
  hasMattersInMultipleStates: boolean;
  utcSubmittedAt: Date | null;
  submittedBy: SmokeballUserId | null;
  submittedByName: string;
  utcLoadStartedAt: Date | null;
  utcLoadCompletedAt: Date | null;
  utcCompletedAt: Date | null;
  utcCreatedAt: Date | null;
  createdBy: SmokeballUserId;
  createdByName: string;
  utcCancelledAt: Date | null;
  cancelledBy: SmokeballUserId | null;
  cancelledByName: string;
  utcLastUpdatedAt: Date;
  lastUpdatedBy: SmokeballUserId | null;
  lastUpdatedByName: string;
  downloadURL: string;
  downloadURLExpiresAt: Date | null;
}

export interface MigrationSetImport {
  migrationImportId: number;
  migrationSetId: number;
  accountId: Guid;
  importType: ImportType;
  fileName: string;
  uploadInProgress: Boolean;
  loadInProgress: Boolean;
  hasUploaded: Boolean;
  embedId: string | null;
  sessionToken: string | null;
  templateKey: TemplateKey;
  uploadTotalRecordCount: number;
  uploadErrorCount: number;
  uploadWarningCount: number;
  loadTotalRecordCount: number;
  totalRecordCountForLoad: number;
  loadErrorCount: number;
  utcLastUpdatedAt: Date;
  lastUpdatedBy: SmokeballUserId | null;
  lastUpdatedByName: string;
  balances?:
    | MatterOpeningBalances
    | UnbilledTimeFeeBalances
    | UnbilledExpenseBalances
    | InvoiceBalances;
}

export interface MigrationSetImportWithOneSchemaSession
  extends MigrationSetImport {
  embedId: string;
  sessionToken: string;
}

export interface MigrationSetImportWithUploadLink extends MigrationSetImport {
  uploadDetails: MigrationUploadLink;
}

export interface MigrationSetWithImports extends MigrationSet {
  imports: MigrationSetImport[];
}

export interface MigrationUploadLink {
  presignedURL: string;
  userJwt: string;
}

export interface MatterOpeningBalances {
  operating?: number;
  credit: number;
  trust: number;
}

export interface UnbilledTimeFeeBalances {
  totalex: number;
  totalinc: number;
  billableex: number;
  billableinc: number;
  nonbillableex: number;
  nonbillableinc: number;
  waivedex: number;
  waivedinc: number;
}

export interface UnbilledExpenseBalances {
  totalex: number;
  totalinc: number;
  billableex: number;
  billableinc: number;
  nonbillableex: number;
  nonbillableinc: number;
  waivedex: number;
  waivedinc: number;
}

export interface InvoiceBalances {
  totalinc: number;
  dueinc: number;
  feeex: number;
  feeinc: number;
  expenseex: number;
  expenseinc: number;
}

export enum DataTypes {
  Staff = "Staff",
  Contacts = "Contacts",
  Matters = "Matters",
  MatterTypes = "Matter Types",
  MatterTags = "Matter Tags",
  MatterRelationships = "Matter Relationships",
  MatterCriticalDates = "Matter Critical Dates",
  MatterArchiveDetails = "Matter Archive Details",
  OpeningBalances = "Opening Balances",
  ActivityTypes = "Activity Types",
  UnbilledTimeAndFees = "Unbilled Time and Fees",
  UnbilledDisbursements = "Unbilled Disbursements",
  Invoices = "Invoices",
  InvoiceItems = "Invoice Items",
  Memos = "Memos",
  Tasks = "Tasks",
  Events = "Events",
  SafeCustodyPackets = "Safe Custody Packets",
  SafeCustodyDocuments = "Safe Custody Documents",
}

export enum DataOperationType {
  Import = "Import",
  Export = "Export",
}

export const SOURCE_SYSTEM_TYPES = [
  "jumpstart",
  "smokeball-export",
  "smokeball-native-export"
] as const

export type SourceSystemType = typeof SOURCE_SYSTEM_TYPES[number];

export const SMOKEBALL_EXPORT_TYPES: readonly SourceSystemType[]  = [
  "smokeball-export",
  "smokeball-native-export",
] as const;
